import { useState } from "react";
import { Send, Calendar, Users, PartyPopper, AlertCircle } from "lucide-react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
// Validation schema for form data
const bookingSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Name is required")
    .max(100, "Name must be less than 100 characters")
    .regex(/^[a-zA-Z\s'-]+$/, "Name can only contain letters, spaces, hyphens, and apostrophes"),
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters"),
  phone: z
    .string()
    .trim()
    .min(1, "Phone number is required")
    .regex(/^[\d\s()+-]+$/, "Please enter a valid phone number")
    .min(10, "Phone number must be at least 10 digits")
    .max(20, "Phone number is too long"),
  eventType: z
    .string()
    .min(1, "Please select an event type"),
  eventDate: z
    .string()
    .optional()
    .refine((date) => {
      if (!date) return true;
      const selectedDate = new Date(date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return selectedDate >= today;
    }, "Event date must be in the future"),
  guestCount: z
    .string()
    .optional()
    .refine((count) => {
      if (!count) return true;
      const num = parseInt(count, 10);
      return !isNaN(num) && num >= 10 && num <= 500;
    }, "Guest count must be between 10 and 500"),
  message: z
    .string()
    .trim()
    .max(1000, "Message must be less than 1000 characters")
    .optional(),
});

type FormErrors = Partial<Record<keyof z.infer<typeof bookingSchema>, string>>;
const BookingForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    eventType: "",
    eventDate: "",
    guestCount: "",
    message: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateForm = (): boolean => {
    try {
      bookingSchema.parse(formData);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: FormErrors = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as keyof FormErrors;
          if (!newErrors[field]) {
            newErrors[field] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('submit-booking', {
        body: formData,
      });

      if (error) {
        throw error;
      }

      setIsSubmitted(true);
      // Clear form data after successful submission
      setFormData({
        name: "",
        email: "",
        phone: "",
        eventType: "",
        eventDate: "",
        guestCount: "",
        message: "",
      });
    } catch (error: any) {
      console.error("Submission error:", error);
      
      // Map specific error codes to user-friendly messages without revealing internal details
      let userMessage = "Unable to submit inquiry. Please try again or contact us at (404) 503-0311.";
      if (error?.context?.status === 429) {
        userMessage = "You've reached the submission limit. Please try again later.";
      } else if (error?.context?.status === 400) {
        userMessage = "Please check your information and try again.";
      }
      
      toast({
        title: "Submission Failed",
        description: userMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error for this field when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors({ ...errors, [name]: undefined });
    }
  };

  const ErrorMessage = ({ field }: { field: keyof FormErrors }) => {
    if (!errors[field]) return null;
    return (
      <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
        <AlertCircle className="w-3 h-3" />
        {errors[field]}
      </p>
    );
  };

  if (isSubmitted) {
    return (
      <section id="booking" className="section-padding bg-primary">
        <div className="container-elegant max-w-2xl text-center">
          <div className="bg-card rounded-2xl p-12 shadow-elevated">
            <div className="w-20 h-20 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <PartyPopper className="w-10 h-10 text-accent" />
            </div>
            <h3 className="font-serif text-3xl font-semibold text-foreground mb-4">
              Thank You!
            </h3>
            <p className="text-muted-foreground text-lg mb-6">
              We've received your inquiry and are excited to help plan your event.
              Our team will reach out within 24 hours to discuss your vision.
            </p>
            <button
              onClick={() => setIsSubmitted(false)}
              className="text-primary font-medium hover:underline"
            >
              Submit another inquiry
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="booking" className="section-padding bg-primary">
      <div className="container-elegant">
        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
          {/* Left Column - Info */}
          <div className="text-primary-foreground">
            <p className="text-primary-foreground/80 font-medium tracking-widest uppercase text-sm mb-3">
              Let's Create Something Special
            </p>
            <h2 className="heading-section mb-6">
              Start Planning Your Event
            </h2>
            <p className="text-primary-foreground/85 text-lg leading-relaxed mb-8">
              Whether you're envisioning an intimate celebration or a grand
              gathering, we're here to bring your vision to life. Fill out the
              form, and our team will reach out within 24 hours to discuss your
              event.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary-foreground/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-primary-foreground" />
                </div>
                <div>
                  <h4 className="font-semibold text-primary-foreground">
                    Flexible Scheduling
                  </h4>
                  <p className="text-primary-foreground/70 text-sm">
                    Weekday and weekend availability
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary-foreground/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-primary-foreground" />
                </div>
                <div>
                  <h4 className="font-semibold text-primary-foreground">
                    Personalized Consultations
                  </h4>
                  <p className="text-primary-foreground/70 text-sm">
                    On-site tastings and menu planning
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="bg-card rounded-2xl p-8 shadow-elevated">
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-foreground mb-2"
                  >
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    maxLength={100}
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
                      errors.name ? "border-red-500" : "border-border"
                    }`}
                    placeholder="Jane Smith"
                  />
                  <ErrorMessage field="name" />
                </div>
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-foreground mb-2"
                  >
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    maxLength={20}
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
                      errors.phone ? "border-red-500" : "border-border"
                    }`}
                    placeholder="(555) 123-4567"
                  />
                  <ErrorMessage field="phone" />
                </div>
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  maxLength={255}
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-lg border bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
                    errors.email ? "border-red-500" : "border-border"
                  }`}
                  placeholder="jane@example.com"
                />
                <ErrorMessage field="email" />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="eventType"
                    className="block text-sm font-medium text-foreground mb-2"
                  >
                    Event Type *
                  </label>
                  <select
                    id="eventType"
                    name="eventType"
                    required
                    value={formData.eventType}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
                      errors.eventType ? "border-red-500" : "border-border"
                    }`}
                  >
                    <option value="">Select an event type</option>
                    <option value="wedding">Wedding Reception</option>
                    <option value="quinceanera">Quinceañera</option>
                    <option value="corporate">Corporate Event</option>
                    <option value="nonprofit">Nonprofit Gala</option>
                    <option value="shower">Shower / Rehearsal</option>
                    <option value="private">Private Celebration</option>
                    <option value="other">Other</option>
                  </select>
                  <ErrorMessage field="eventType" />
                </div>
                <div>
                  <label
                    htmlFor="eventDate"
                    className="block text-sm font-medium text-foreground mb-2"
                  >
                    Tentative Date
                  </label>
                  <input
                    type="date"
                    id="eventDate"
                    name="eventDate"
                    min={new Date().toISOString().split('T')[0]}
                    value={formData.eventDate}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
                      errors.eventDate ? "border-red-500" : "border-border"
                    }`}
                  />
                  <ErrorMessage field="eventDate" />
                </div>
              </div>

              <div>
                <label
                  htmlFor="guestCount"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  Estimated Guest Count
                </label>
                <input
                  type="number"
                  id="guestCount"
                  name="guestCount"
                  min="10"
                  max="500"
                  value={formData.guestCount}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-lg border bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
                    errors.guestCount ? "border-red-500" : "border-border"
                  }`}
                  placeholder="100"
                />
                <ErrorMessage field="guestCount" />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  Tell Us About Your Vision
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={1000}
                  value={formData.message}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-lg border bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none ${
                    errors.message ? "border-red-500" : "border-border"
                  }`}
                  placeholder="Share any special requests, dietary needs, or questions..."
                />
                <ErrorMessage field="message" />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-gold py-4 rounded-lg font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-70"
              >
                {isSubmitting ? (
                  "Sending..."
                ) : (
                  <>
                    Send Inquiry <Send className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingForm;
